import type { AccessCensus, AnalyticsWindow } from './analyticsApi';

export const ANALYTICS_WINDOWS = [
  { key: '24h', label: 'Last 24 hours' },
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: 'all', label: 'All time' },
] as const;

const THIRTY_DAYS = 2;
const ALL_TIME = 3;

/** Grey says a zero is unremarkable; yellow says a zero is a possible problem. */
export type RowTone = 'grey' | 'yellow';

export type AnalyticsCell = {
  count: number;
  people?: number;
};

type DropOff = {
  from: readonly string[];
  note: (starts: { people?: number; times: number }) => string;
};

export type AnalyticsRowSpec = {
  label: string;
  types: readonly string[];
  caption?: string;
  /** Shown only when more than one of `types` actually arrives in the snapshot. */
  mergedNote?: string;
  tone?: RowTone;
  dropOff?: DropOff;
};

export type AnalyticsSectionSpec = {
  title: string;
  rows: readonly AnalyticsRowSpec[];
};

export type AnalyticsRow = {
  key: string;
  label: string;
  caption?: string;
  neverFired: boolean;
  tone: RowTone;
  cells: AnalyticsCell[];
};

export type AnalyticsSection = {
  title: string;
  rows: AnalyticsRow[];
};

const ISSUE_REPORT_OPENED = [
  'reclassification.issue_report_opened',
  'exit.issue_report_opened',
] as const;

/**
 * Every event type we name in plain language, in the order the report reads.
 * This duplicates `knownEventTypes` in the server's event.go on purpose; the
 * "Other" fallback in analyticsSections is what keeps the duplication safe.
 */
export const ANALYTICS_SECTIONS: readonly AnalyticsSectionSpec[] = [
  {
    title: 'Signing in',
    rows: [{ label: 'Signed in', types: ['user.logged_in'] }],
  },
  {
    title: 'Student profiles',
    rows: [{ label: "Opened a student's profile", types: ['student_profile.opened'] }],
  },
  {
    title: 'Student action plans',
    rows: [
      { label: "Opened a student's action plan", types: ['plan.opened'] },
      { label: 'Generated plans in bulk after an upload', types: ['plans.generated'] },
      { label: 'Saved a plan as a snapshot', types: ['plan_snapshot.requested'] },
    ],
  },
  {
    title: 'AI suggestions',
    rows: [
      { label: 'Asked for focus areas by language domain', types: ['focus_by_domain.requested'] },
      { label: 'Asked for suggested accommodations', types: ['accommodations.suggested'] },
    ],
  },
  {
    title: 'Program evaluation',
    rows: [
      {
        label: 'Asked for a program evaluation',
        types: ['program_evaluation.generation_requested'],
      },
      { label: 'A program evaluation finished', types: ['program_evaluation.generated'] },
      { label: 'Asked for one to be redone', types: ['program_evaluation.regen_requested'] },
    ],
  },
  {
    title: 'Data and rosters',
    rows: [
      { label: 'Uploaded a file', types: ['file.uploaded'] },
      { label: 'Matched a roster to students', types: ['roster.reconciled'] },
      { label: 'Deleted a file', types: ['file.deleted'] },
    ],
  },
  {
    title: 'Problems reported with our recommendations',
    rows: [
      {
        label: 'Started a report about an exit recommendation',
        types: ISSUE_REPORT_OPENED,
        // The rollup folds the renamed pair into one name; a snapshot from
        // before it did still carries both, and only then is the sum worth
        // calling out.
        mergedNote: 'Two event names combined — this action was renamed part-way through',
      },
      {
        label: 'Backed out of a report',
        types: ['reclassification.issue_report_cancelled'],
      },
      {
        label: 'Sent us the report',
        types: ['reclassification.issue_report_submitted'],
        tone: 'yellow',
        dropOff: {
          from: ISSUE_REPORT_OPENED,
          note: ({ people, times }) => {
            // Without a distinct-user figure all we know is how many starts
            // there were, and two starts can be one determined person.
            if (people === undefined) {
              return times === 1
                ? '1 report was started and never finished'
                : `${times} reports were started and none were finished`;
            }
            return people === 1
              ? '1 person started a report and did not finish it'
              : `${people} people started a report and none of them finished it`;
          },
        },
      },
    ],
  },
];

/** Access failures get their own card, so they never reach the activity table. */
export const ANALYTICS_PROBLEM_ROWS: readonly AnalyticsRowSpec[] = [
  {
    label: 'Turned away from a page',
    types: ['access.denied'],
    caption: 'Someone tried to open something they are not allowed to see',
  },
  {
    label: 'Something broke while checking access',
    types: ['access.error'],
  },
];

/**
 * Types we know about and deliberately leave off the page. Listing them keeps
 * them out of "Other", which would otherwise surface them by the back door.
 */
export const ANALYTICS_EXCLUDED_TYPES: readonly string[] = [
  // Counts students exited — a district outcome, not a staff usage signal.
  'reclassification.confirmed',
  'exit.confirmed',
  // Super-admin-only action, excluded with the rest of staff activity.
  'admin.upload.viewed',
  // Headline tile 2 is where this one is reported, the same way the access
  // events are reported in the problems card rather than the table.
  'program_evaluation.viewed',
  // Retired with the onboarding requirement (card 4435); the all-time rollup
  // still carries the records that were written before that.
  'onboarding.completed',
  'onboarding.reset',
];

function windowsByKey(windows: AnalyticsWindow[]): Map<string, AnalyticsWindow> {
  return new Map(windows.map((window) => [window.window, window]));
}

function cellsFor(windows: AnalyticsWindow[], types: readonly string[]): AnalyticsCell[] {
  const byKey = windowsByKey(windows);
  return ANALYTICS_WINDOWS.map(({ key }) => {
    const entries = types.flatMap((type) => {
      const entry = byKey.get(key)?.byType.find((candidate) => candidate.type === type);
      return entry ? [entry] : [];
    });
    const count = entries.reduce((sum, entry) => sum + entry.count, 0);
    const counted = entries.filter((entry) => typeof entry.people === 'number');
    // A missing `people` means unknown, not zero, so only report one when the
    // payload actually carried it.
    if (counted.length === 0) return { count };
    // Two names for one action can hold the same human twice, and a per-type
    // figure carries no way to tell. Only the rollup can take that union, so
    // anything it has not already merged stays unknown rather than overstated.
    if (counted.length > 1) return { count };
    return { count, people: counted[0]?.people };
  });
}

function startsFrom(cell: AnalyticsCell | undefined): { people?: number; times: number } {
  return { people: cell?.people, times: cell?.count ?? 0 };
}

function namesReported(windows: AnalyticsWindow[], types: readonly string[]): number {
  return types.filter((type) =>
    windows.some((window) => window.byType.some((entry) => entry.type === type)),
  ).length;
}

function rowCaption(
  windows: AnalyticsWindow[],
  spec: AnalyticsRowSpec,
  neverFired: boolean,
): string | undefined {
  const started = spec.dropOff
    ? startsFrom(cellsFor(windows, spec.dropOff.from)[ALL_TIME])
    : { times: 0 };
  if (spec.dropOff && neverFired && started.times > 0) return spec.dropOff.note(started);
  if (spec.mergedNote && namesReported(windows, spec.types) > 1) return spec.mergedNote;
  return spec.caption;
}

function buildRow(windows: AnalyticsWindow[], spec: AnalyticsRowSpec): AnalyticsRow {
  const cells = cellsFor(windows, spec.types);
  const neverFired = cells.every((cell) => cell.count === 0);
  return {
    key: spec.types.join('+'),
    label: spec.label,
    caption: rowCaption(windows, spec, neverFired),
    neverFired,
    tone: spec.tone ?? 'grey',
    cells,
  };
}

function mappedTypes(): Set<string> {
  return new Set([
    ...ANALYTICS_SECTIONS.flatMap((section) => section.rows.flatMap((row) => [...row.types])),
    ...ANALYTICS_PROBLEM_ROWS.flatMap((row) => [...row.types]),
    ...ANALYTICS_EXCLUDED_TYPES,
  ]);
}

function unmappedRows(windows: AnalyticsWindow[]): AnalyticsRow[] {
  const known = mappedTypes();
  const seen = new Set<string>();
  for (const window of windows) {
    for (const entry of window.byType) {
      if (!known.has(entry.type)) seen.add(entry.type);
    }
  }
  return [...seen]
    .map((type) => buildRow(windows, { label: type, types: [type] }))
    .sort(
      (a, b) =>
        (b.cells[ALL_TIME]?.count ?? 0) - (a.cells[ALL_TIME]?.count ?? 0) ||
        a.label.localeCompare(b.label),
    );
}

export function analyticsSections(windows: AnalyticsWindow[]): AnalyticsSection[] {
  const sections = ANALYTICS_SECTIONS.map((section) => ({
    title: section.title,
    rows: section.rows.map((spec) => buildRow(windows, spec)),
  }));
  const other = unmappedRows(windows);
  return other.length > 0 ? [...sections, { title: 'Other', rows: other }] : sections;
}

export function analyticsProblemRows(windows: AnalyticsWindow[]): AnalyticsRow[] {
  return ANALYTICS_PROBLEM_ROWS.map((spec) => buildRow(windows, spec));
}

export type HeadlineTile = {
  key: string;
  figure: number | null;
  /** The tile-1 figure this is a share of, or null to render a bare count. */
  ofThose: number | null;
  label: string;
  caption?: string;
};

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

// Nothing in the month means nobody, but a count without people is a payload
// that never said who, and that stays unknown.
function peopleOf(cell: AnalyticsCell): number | null {
  return cell.people ?? (cell.count === 0 ? 0 : null);
}

export function analyticsHeadline(windows: AnalyticsWindow[]): HeadlineTile[] {
  const month = (type: string) => cellsFor(windows, [type])[THIRTY_DAYS] ?? { count: 0 };
  const signIns = month('user.logged_in');
  const views = month('program_evaluation.viewed');
  const plans = month('plan.opened');
  const signedIn = peopleOf(signIns);

  // Only a figure we can prove is no larger than tile 1 reads as a share of it.
  // A share of nobody is not worth printing as "0 of those 0".
  const shareOf = (figure: number | null) =>
    signedIn !== null && signedIn > 0 && figure !== null && figure <= signedIn ? signedIn : null;

  return [
    {
      key: 'signed-in',
      figure: signedIn,
      ofThose: null,
      label: 'People who signed in',
      caption:
        signIns.count > 0
          ? `Out of ${plural(signIns.count, 'sign-in', 'sign-ins')}, once Flashlight staff are dropped`
          : undefined,
    },
    {
      key: 'program-evaluation',
      figure: peopleOf(views),
      ofThose: shareOf(peopleOf(views)),
      label: 'People who opened the Program Evaluation',
    },
    {
      key: 'action-plans',
      figure: peopleOf(plans),
      ofThose: shareOf(peopleOf(plans)),
      label: 'People who opened at least one student action plan',
      caption:
        plans.count > 0
          ? `They opened ${plural(plans.count, 'plan', 'plans')} between them`
          : undefined,
    },
  ];
}

export type AccessTile = {
  key: string;
  figure: number;
  of: number;
  label: string;
  caption?: string;
};

export function analyticsAccessTiles(access: AccessCensus): AccessTile[] {
  const { districts, admins, teachers } = access;
  const nobody = districts.withAccess === 0;
  return [
    {
      key: 'districts',
      figure: districts.withAccess,
      of: districts.total,
      label: 'Districts with someone granted Insight access',
      caption: nobody
        ? 'Nobody has been granted access yet'
        : `${districts.withAdminAccess} with an admin granted, ${districts.withTeacherAccess} with a teacher granted`,
    },
    { key: 'admins', figure: admins.granted, of: admins.total, label: 'District admins granted Insight access' },
    { key: 'teachers', figure: teachers.granted, of: teachers.total, label: 'Teachers granted Insight access' },
  ];
}

export function hasAnyEvents(windows: AnalyticsWindow[]): boolean {
  return windows.some((window) => window.byType.length > 0);
}

export { formatUpdatedAgo } from '../../utils/updatedAgo';

export function formatComputedAt(iso: string | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: 'UTC',
  })} UTC`;
}
